import { useCreateStandardIndustryClassification } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DataConflictModal } from "@/components/commons/DataConflictModal";
import { useDialog } from "@/hooks/useDialog";
import { useStandardIndustryConflictModal } from "@/features/master-data/hooks/useStandardIndustryConflictModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { ClassificationCodeField } from "@/features/master-data/components/StandardIndustry/ClassificationCodeField";
import { STANDARD_INDUSTRY_LEVEL_LABEL, TStandardIndustryLevel } from "@/features/master-data/constants/standardIndustry";
import { makeStandardIndustryClassificationSchema } from "@/features/master-data/schema/masterDataStandardIndustrySchema";
import { TStandardIndustryClassificationForm } from "@/features/master-data/types/masterDataStandardIndustryForm";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TClassificationFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupId: number;
  parentId: number | null;
  level: TStandardIndustryLevel;
  parentFullCode: string; // prefix used to preview the resulting fullCode
  parentUpdatedAt?: string; // parent node's updatedAt for optimistic concurrency on create; omitted for top-level (parent = group)
};

export const ClassificationFormModal: FC<TClassificationFormModalProps> = ({
  isOpen,
  onClose,
  groupId,
  parentId,
  level,
  parentFullCode,
  parentUpdatedAt,
}) => {
  const schema = useMemo(() => makeStandardIndustryClassificationSchema(level), [level]);

  const createMutation = useCreateStandardIndustryClassification();

  const methods = useForm<TStandardIndustryClassificationForm>({
    resolver: yupResolver(schema),
    defaultValues: { code: "", name: "" },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const {
    control,
    formState: { isDirty },
  } = methods;

  const confirmClose = useDialog();
  const conflict = useStandardIndustryConflictModal(onClose);

  const handleRequestClose = async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    const isConfirmed = await confirmClose.show();
    if (isConfirmed) {
      onClose();
    }
  };

  const onSubmit = async (data: TStandardIndustryClassificationForm) => {
    try {
      const rs = await createMutation.mutateAsync({ groupId, parentId, level, code: data.code, name: data.name, updatedAt: parentUpdatedAt });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      onClose();
    } catch (error) {
      if (conflict.handleConflictError(error)) {
        return;
      }
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      width={600}
      title={`${STANDARD_INDUSTRY_LEVEL_LABEL[level]}の追加`}
      isOpen={isOpen}
      enableFooter={false}
      onClose={handleRequestClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={schema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={12}>
            <ClassificationCodeField control={control} parentFullCode={parentFullCode} />
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="name">
              <Input />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={handleRequestClose}>
            キャンセル
          </Button>
          <Button type="submit" isLoading={createMutation.isPending}>
            保存
          </Button>
        </Stack>
      </Form>
      <ConfirmSaveModal open={confirmClose.open} onOk={confirmClose.confirm} onClose={confirmClose.cancel}>
        <Typography>入力したデータがなくなりますが、よろしいですか。</Typography>
      </ConfirmSaveModal>
      <DataConflictModal
        open={conflict.isConflictOpen}
        name={conflict.conflictName}
        onConfirm={conflict.confirmReload}
        onCancel={conflict.cancelConflict}
      />
    </Modal>
  );
};
